import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import { Formik } from 'formik'
import { motion, AnimatePresence } from 'framer-motion'
import React, { useState, useRef, useEffect } from 'react'

import scrollToEl from '../../lib/scroll-to-el'
import SubmissionError from './error-message'
import SuccessMessage from './success-message'

const FormContainer = ({
  children,
  schema,
  initialValues,
  handleSubmit,
  prewarm,
}) => {
  const [interactionReported, setInteractionReported] = useState(false)
  const formPanelRef = useRef(null)

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (window.hasOwnProperty('recaptchaObserver')) {
      const currentFormPanelRef = formPanelRef.current
      if (!window.hasOwnProperty('grecaptcha')) {
        window.recaptchaObserver.observe(currentFormPanelRef)
        return () => {
          window.recaptchaObserver.unobserve(currentFormPanelRef)
        }
      }
    }
  }, [])

  const reportFormInteraction = () => {
    // TODO report enquiryType
    window.dataLayer.push({
      event: 'form-interact',
    })
    setInteractionReported(true)
    prewarm()
    // TODO re-warm on interaction after threshold time period
  }

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={schema}
      validateOnChange={false}
      onSubmit={async (formData, { setStatus }) => {
        try {
          await handleSubmit(formData)
          setStatus({ success: true })
          // TODO record successful submission so that subsequent submissions from same user can be flagged
        } catch (submissionError) {
          setStatus({ submissionError })
        } finally {
          scrollToEl(formPanelRef)
        }
      }}
    >
      {({ errors, dirty, isSubmitting, status = {} }) => {
        if (!interactionReported && dirty) reportFormInteraction()
        const hasError = status.hasOwnProperty('submissionError')

        return (
          <FormPanel
            ref={formPanelRef}
            animate={
              isSubmitting
                ? 'submitting'
                : hasError
                  ? 'error'
                  : status.success
                    ? 'success'
                    : 'editing'
            }
          >
            <AnimatePresence>
              {status.success && (
                <FormMessage
                  animate={isSubmitting ? 'submitting' : 'visible'}
                  true
                  key="submissionSuccess"
                >
                  <SuccessMessage />
                </FormMessage>
              )}
              {hasError && (
                <FormMessage
                  animate={isSubmitting ? 'submitting' : 'visible'}
                  key="submissionError"
                >
                  <SubmissionError message={status.submissionError?.message} />
                </FormMessage>
              )}
            </AnimatePresence>
            <motion.div variants={formWrapperVariants}>
              {children({ dirty, isSubmitting })}
            </motion.div>
            <StatusBarWrapper>
              <StatusBar
                isSubmitting={isSubmitting}
                variants={statusBarVariants}
                initial="hidden"
                error={hasError}
              />
            </StatusBarWrapper>
          </FormPanel>
        )
      }}
    </Formik>
  )
}

const FormMessage = ({ children, ...passthrough }) => (
  <motion.div
    variants={modalVariants}
    initial="hidden"
    exit="hidden"
    {...passthrough}
  >
    {children}
  </motion.div>
)

const formWrapperVariants = {
  success: {
    height: '0px',
    opacity: 0,
  },
  error: {
    opacity: 1,
  },
  submitting: {
    opacity: 0.3,
  },
  editing: {
    height: 'auto',
    opacity: 1,
  },
}

const modalVariants = {
  visible: {
    opacity: 1,
    scale: 1,
  },
  submitting: {
    opacity: 0.5,
  },
  initial: {
    opacity: 0,
    scale: 0,
  },
}

const FormPanel = styled(motion.div)`
  position: relative;
`

const progressAnimation = keyframes`
0% {
    background-position: 0 0;
  }
100% {
  background-position: 50px 50px;
}`

const statusBarHiddenVariants = {
  scaleY: 0.8,
  y: '0px',
  opacity: 0,
}

const statusBarVariants = {
  hidden: statusBarHiddenVariants,
  success: statusBarHiddenVariants,
  error: statusBarHiddenVariants,
  submitting: {
    scaleY: 1,
    y: '0px',
    opacity: 1,
  },
}

const StatusBarWrapper = styled.div`
  display: none;
  position: absolute;
  left: 0;
  right: 0;
  bottom: -24px;
  display: flex;
  justify-content: center;
  overflow: hidden;
  height: 16px;
`

const StatusBar = styled(motion.div, {
  shouldForwardProp: (prop) => prop !== 'isSubmitting' && prop !== 'error',
})`
  height: 16px;
  border-radius: 99px;
  flex: 0 1 240px;

  background: ${(p) =>
    !p.isSubmitting && p.error
      ? p.theme.colors.alertIntense
      : p.theme.colors.secondary};
  z-index: 99;

  background-size: 50px 50px;
  background-image: linear-gradient(
    -45deg,
    rgba(0, 0, 0, 0.1) 25%,
    transparent 25%,
    transparent 50%,
    rgba(0, 0, 0, 0.1) 50%,
    rgba(0, 0, 0, 0.1) 75%,
    transparent 75%
  );

  animation: ${(p) => (p.isSubmitting ? progressAnimation : 'none')} 1.5s linear
    infinite;
`

export default FormContainer
